/**
 * ✅ FERP TEMPLATE REFINEMENT HOOK
 *
 * Sprint 8 LLM A Track - Day 5
 *
 * Template Refinement for Low-Confidence Fields
 * - Human review workflow for <0.85 confidence fields
 * - Evidence attribution display
 * - Field-level justification capture
 * - Confidence recalculation after refinement
 * - Multi-LLM disagreement resolution
 * - Immutable refinement audit trail
 *
 * ARCHITECTURE:
 * - Data-driven (all data from backend repository)
 * - GraphQL mutations only
 * - Backend ID generation only
 * - Multi-tenant isolation
 * - Immutable audit trail for refinements
 *
 * CONFIDENCE THRESHOLDS:
 * - >= 0.90: Auto-approve (no human review needed)
 * - 0.85-0.89: Flagged for optional review
 * - < 0.85: Required human review
 * - < 0.70: Critical - requires expert review
 *
 * USAGE:
 * ```tsx
 * const { refineField, refinementHistory } = useTemplateRefinement(templateId);
 * await refineField('business.name', 'Patient Intake', 'Original value was ambiguous');
 * ```
 */

import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';
import { useAuth } from '../../security/useAuth';
import { useAuditLogger } from '../../hooks/useAuditLogger';
import { notifications } from '@mantine/notifications';

// ============================================================
// GraphQL Mutations
// ============================================================

const REFINE_TEMPLATE = gql`
  mutation RefineTemplate($input: TemplateRefinementInput!) {
    refineTemplate(input: $input) {
      templateId
      refinedFields {
        fieldPath
        originalValue
        refinedValue
        originalConfidence
        newConfidence
        justification
        evidenceReferences
        refinedBy
        refinedAt
        llmDisagreements {
          provider
          extractedValue
          confidence
        }
      }
      updatedConfidence {
        overall
        business
        ui
        primitives
        mappings
        compliance
      }
      requiresAdditionalReview
      reviewNotes
    }
  }
`;

const GET_REFINEMENT_CANDIDATES = gql`
  query GetRefinementCandidates($templateId: String!, $tenantId: String!) {
    getRefinementCandidates(templateId: $templateId, tenantId: $tenantId) {
      fieldPath
      currentValue
      confidence
      llmResults {
        provider
        extractedValue
        confidence
      }
      evidenceReferences {
        sourceDocument
        pageNumber
        excerpt
        relevanceScore
      }
      recommendedAction
      criticality
    }
  }
`;

// ============================================================
// TypeScript Types
// ============================================================

export interface TemplateRefinementInput {
  templateId: string;
  tenantId: string;
  refinedFields: RefinedField[];
  reviewNotes?: string;
  refinedBy: string;
}

export interface RefinedField {
  fieldPath: string;
  originalValue: any;
  refinedValue: any;
  justification: string;
  evidenceReferences?: string[];
}

export interface RefinedFieldResult {
  fieldPath: string;
  originalValue: any;
  refinedValue: any;
  originalConfidence: number;
  newConfidence: number;
  justification: string;
  evidenceReferences: string[];
  refinedBy: string;
  refinedAt: string;
  llmDisagreements: LLMDisagreement[];
}

export interface LLMDisagreement {
  provider: string;
  extractedValue: any;
  confidence: number;
}

export interface UpdatedConfidence {
  overall: number;
  business: number;
  ui: number;
  primitives: number;
  mappings: number;
  compliance: number;
}

export interface RefinementResult {
  templateId: string;
  refinedFields: RefinedFieldResult[];
  updatedConfidence: UpdatedConfidence;
  requiresAdditionalReview: boolean;
  reviewNotes: string;
}

export interface RefinementCandidate {
  fieldPath: string;
  currentValue: any;
  confidence: number;
  llmResults: {
    provider: string;
    extractedValue: any;
    confidence: number;
  }[];
  evidenceReferences: {
    sourceDocument: string;
    pageNumber: number;
    excerpt: string;
    relevanceScore: number;
  }[];
  recommendedAction: 'APPROVE' | 'REFINE' | 'REJECT' | 'EXPERT_REVIEW';
  criticality: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

export interface RefinementHistoryEntry {
  fieldPath: string;
  timestamp: string;
  refinedBy: string;
  originalValue: any;
  refinedValue: any;
  justification: string;
  confidenceChange: number;
}

// ============================================================
// Hook Implementation
// ============================================================

export function useTemplateRefinement(templateId: string | null) {
  const { tenantId } = useCurrentTenant();
  const { user } = useAuth();
  const { logUpdate } = useAuditLogger();

  const [refineTemplate, { loading: mutationLoading }] = useMutation<Record<string, any>>(REFINE_TEMPLATE);

  const [refinementHistory, setRefinementHistory] = useState<RefinementHistoryEntry[]>([]);
  const [pendingRefinements, setPendingRefinements] = useState<Map<string, RefinedField>>(new Map());
  const [error, setError] = useState<Error | null>(null);

  /**
   * Refine a single field with justification
   *
   * CRITICAL: Captures human review decision for audit trail
   */
  const refineField = useCallback(
    async (
      fieldPath: string,
      refinedValue: any,
      justification: string,
      evidenceReferences?: string[]
    ): Promise<void> => {
      if (!templateId || !tenantId) {
        throw new Error('Template ID and tenant ID are required');
      }

      const originalValue = pendingRefinements.get(fieldPath)?.originalValue;

      const refinement: RefinedField = {
        fieldPath,
        originalValue,
        refinedValue,
        justification,
        evidenceReferences,
      };

      // Add to pending refinements (batched submission)
      setPendingRefinements((prev) => {
        const updated = new Map(prev);
        updated.set(fieldPath, refinement);
        return updated;
      });

      notifications.show({
        title: 'Field Queued for Refinement',
        message: `${fieldPath} will be updated when you submit`,
        color: 'blue',
      });
    },
    [templateId, tenantId, pendingRefinements]
  );

  /**
   * Submit all pending refinements in a single batch
   *
   * REPOSITORY PATTERN: Single GraphQL mutation for atomic update
   */
  const submitRefinements = useCallback(
    async (reviewNotes?: string): Promise<RefinementResult> => {
      if (!templateId || !tenantId) {
        throw new Error('Template ID and tenant ID are required');
      }

      if (pendingRefinements.size === 0) {
        throw new Error('No pending refinements to submit');
      }

      setError(null);

      try {
        const input: TemplateRefinementInput = {
          templateId,
          tenantId,
          refinedFields: Array.from(pendingRefinements.values()),
          reviewNotes,
          refinedBy: user?.id || 'unknown',
        };

        const { data } = await refineTemplate({
          variables: { input },
        });

        if (!data?.refineTemplate) {
          throw new Error('Failed to refine template');
        }

        const result: RefinementResult = data.refineTemplate;

        // Update refinement history (immutable append)
        const newHistoryEntries: RefinementHistoryEntry[] = result.refinedFields.map((field) => ({
          fieldPath: field.fieldPath,
          timestamp: field.refinedAt,
          refinedBy: field.refinedBy,
          originalValue: field.originalValue,
          refinedValue: field.refinedValue,
          justification: field.justification,
          confidenceChange: field.newConfidence - field.originalConfidence,
        }));

        setRefinementHistory((prev) => [...prev, ...newHistoryEntries]);

        // Clear pending refinements
        setPendingRefinements(new Map());

        // Audit log (HIPAA/SOX compliance)
        await logUpdate('ChaosTemplate', templateId, {
          fieldsRefined: result.refinedFields.length,
          confidenceChange: {
            before: result.refinedFields[0]?.originalConfidence || 0,
            after: result.updatedConfidence.overall,
          },
          reviewNotes,
          action: 'TEMPLATE_REFINEMENT',
          severity: result.requiresAdditionalReview ? 'WARNING' : 'INFO',
        });

        // Show success notification
        notifications.show({
          title: 'Template Refined',
          message: `${result.refinedFields.length} field(s) updated. Confidence: ${Math.round(result.updatedConfidence.overall * 100)}%`,
          color: result.updatedConfidence.overall >= 0.85 ? 'green' : 'orange',
        });

        return result;
      } catch (err) {
        const refinementError = err instanceof Error ? err : new Error('Failed to refine template');
        setError(refinementError);

        notifications.show({
          title: 'Refinement Failed',
          message: refinementError.message,
          color: 'red',
        });

        throw refinementError;
      }
    },
    [templateId, tenantId, user, pendingRefinements, refineTemplate, logUpdate]
  );

  /**
   * Cancel pending refinement for a field
   */
  const cancelRefinement = useCallback((fieldPath: string): void => {
    setPendingRefinements((prev) => {
      const updated = new Map(prev);
      updated.delete(fieldPath);
      return updated;
    });
  }, []);

  /**
   * Clear all pending refinements
   */
  const clearPendingRefinements = useCallback((): void => {
    setPendingRefinements(new Map());
  }, []);

  /**
   * Check if field has pending refinement
   */
  const hasPendingRefinement = useCallback(
    (fieldPath: string): boolean => {
      return pendingRefinements.has(fieldPath);
    },
    [pendingRefinements]
  );

  /**
   * Get pending refinement for field
   */
  const getPendingRefinement = useCallback(
    (fieldPath: string): RefinedField | undefined => {
      return pendingRefinements.get(fieldPath);
    },
    [pendingRefinements]
  );

  /**
   * Get refinement history for field
   */
  const getFieldHistory = useCallback(
    (fieldPath: string): RefinementHistoryEntry[] => {
      return refinementHistory.filter((entry) => entry.fieldPath === fieldPath);
    },
    [refinementHistory]
  );

  /**
   * Calculate overall confidence improvement
   */
  const getConfidenceImprovement = useCallback((): number => {
    if (refinementHistory.length === 0) return 0;
    return refinementHistory.reduce((sum, entry) => sum + entry.confidenceChange, 0) / refinementHistory.length;
  }, [refinementHistory]);

  return {
    // State
    pendingRefinements: Array.from(pendingRefinements.values()),
    pendingRefinementsMap: pendingRefinements,
    refinementHistory,
    isRefining: mutationLoading,
    error,

    // Actions
    refineField,
    submitRefinements,
    cancelRefinement,
    clearPendingRefinements,

    // Queries
    hasPendingRefinement,
    getPendingRefinement,
    getFieldHistory,

    // Computed
    hasPendingChanges: pendingRefinements.size > 0,
    pendingCount: pendingRefinements.size,
    totalRefinements: refinementHistory.length,
    confidenceImprovement: getConfidenceImprovement(),
  };
}

/**
 * Hook variant for fetching refinement candidates
 * (fields that need human review based on confidence)
 */
export function useRefinementCandidates(templateId: string | null) {
  const { tenantId } = useCurrentTenant();
  const [candidates, setCandidates] = useState<RefinementCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCandidates = useCallback(async () => {
    if (!templateId || !tenantId) return;

    setLoading(true);
    setError(null);

    try {
      // TODO: Implement GraphQL query when backend endpoint is ready
      // For now, return empty array
      setCandidates([]);
    } catch (err) {
      const fetchError = err instanceof Error ? err : new Error('Failed to fetch candidates');
      setError(fetchError);
    } finally {
      setLoading(false);
    }
  }, [templateId, tenantId]);

  /**
   * Filter candidates by criticality level
   */
  const getCriticalCandidates = useCallback((): RefinementCandidate[] => {
    return candidates.filter((c) => c.criticality === 'CRITICAL' || c.criticality === 'HIGH');
  }, [candidates]);

  /**
   * Filter candidates by recommended action
   */
  const getCandidatesByAction = useCallback(
    (action: RefinementCandidate['recommendedAction']): RefinementCandidate[] => {
      return candidates.filter((c) => c.recommendedAction === action);
    },
    [candidates]
  );

  return {
    candidates,
    loading,
    error,
    fetchCandidates,
    getCriticalCandidates,
    getCandidatesByAction,
    requiresReview: candidates.length > 0,
    criticalCount: candidates.filter((c) => c.criticality === 'CRITICAL').length,
  };
}
